import {
  AddressingResponse,
  BankAccountResponse,
  ContractResponse,
  CreditHolderResponse,
  ESubscriptionMode,
  InsuranceResponse,
  MeshType,
  OfferResponse,
  PhysicalPersonResponse,
  VehicleResponse
} from '../../pages/contract-action/contract-response.model';
import { formatLocaleDate, formatLocaleDateMonthYear } from '../../helpers/date.helper';
import { ReferenceDataStore, ReferenceValue } from '../../reference/reference.model';
import { formatCurrency } from '@angular/common';
import { getReferenceValue } from '../../helpers/reference.helper';
import { TranslateService } from '@ngx-translate/core';
import {
  AccountOwner,
  formatValue,
  getBankAccountContent,
  getMatrimonialStatusLabel,
  isInEnum,
  MatrimonialStatus,
  NoValue,
  ProfessionCompany,
  RecapCard,
  RecapCardFormat
} from '@tribeauto/ngx-mobilite-material';
import { transformNonImportedRegistration } from '../../helpers/registration.helper';
import { Validators } from '@angular/forms';

const cardTitle = 'application.recap-card.card-title';
const attributeTitle = 'application.recap-card.attribute-title';
const attributeContent = 'application.recap-card.attribute-content';

const attributeContentMonthDuration = `${attributeContent}.month-duration`;
const attributeContentOwner = `${attributeContent}.owner`;
const attributeContentTenant = `${attributeContent}.tenant`;
const attributeContentYes = `${attributeContent}.yes`;
const attributeContentNo = `${attributeContent}.no`;
const attributeTitleProduct = `${attributeTitle}.product`;
const attributeTitleCivility = `${attributeTitle}.civility`;
const attributeTitleFirstName = `${attributeTitle}.first-name`;
const attributeTitleLastName = `${attributeTitle}.last-name`;
const attributeTitleNationality = `${attributeTitle}.nationality`;
const attributeTitleIdentityPieceIdentifier = `${attributeTitle}.identity-piece-identifier`;
const attributeTitleNumber = `${attributeTitle}.number`;
const attributeTitleEmailAddress = `${attributeTitle}.email-address`;
const attributeTitleCellPhoneNumber = `${attributeTitle}.cell-phone-number`;
const attributeTitleBirthCountry = `${attributeTitle}.birth-country`;
const attributeTitleBirthDate = `${attributeTitle}.birth-date`;
const attributeTitleBirthDepartment = `${attributeTitle}.birth-department`;
const attributeTitleBirthCity = `${attributeTitle}.birth-city`;
const attributeTitleAddress = `${attributeTitle}.address`;
const attributeTitlePostCode = `${attributeTitle}.post-code`;
const attributeTitleCity = `${attributeTitle}.city`;
const attributeTitleAdditionalAddress = `${attributeTitle}.additional-address`;
const attributeTitleHousingMode = `${attributeTitle}.housing-mode`;
const attributeTitleSince = `${attributeTitle}.since`;
const attributeTitleSituation = `${attributeTitle}.situation`;
const attributeTitleDependentChildrenNumber = `${attributeTitle}.dependent-children-number`;
const attributeTitleMatrimonialRegime = `${attributeTitle}.matrimonial-regime`;
const attributeTitleMonthlyNetIncome = `${attributeTitle}.monthly-net-income`;
const attributeTitleSocialBenefits = `${attributeTitle}.social-benefits`;
const attributeTitleOtherIncomes = `${attributeTitle}.other-incomes`;
const attributeTitleHousingExpense = `${attributeTitle}.housing-expense`;
const attributeTitleAlimonyPaid = `${attributeTitle}.alimony-paid`;
const attributeTitleOtherExpenses = `${attributeTitle}.other-expenses`;
const attributeTitleActivitySector = `${attributeTitle}.activity-sector`;
const attributeTitleJobSituation = `${attributeTitle}.job-situation`;
const attributeTitleContractType = `${attributeTitle}.contract-type`;
const attributeTitleQualification = `${attributeTitle}.qualification`;
const attributeTitleMainActivityCountry = `${attributeTitle}.main-activity-country`;
const attributeTitleActivity = `${attributeTitle}.activity`;
const attributeTitleComplementary = `${attributeTitle}.complementary`;
const attributeTitleCountry = `${attributeTitle}.country`;
const attributeTitlePhone = `${attributeTitle}.phone`;
const attributeTitleSeniority = `${attributeTitle}.seniority`;
const attributeTitleSiren = `${attributeTitle}.siren`;
const attributeTitleCreationDate = `${attributeTitle}.creation-date`;
const attributeTitleLegalForm = `${attributeTitle}.legal-form`;
const attributeTitleAdminCourt = `${attributeTitle}.admin-court`;
const attributeTitleCodeNaf = `${attributeTitle}.code-naf`;
const attributeTitleCapital = `${attributeTitle}.capital`;
const attributeTitleEmployeeNumber = `${attributeTitle}.employee-number`;
const attributeTitleEmployerName = `${attributeTitle}.employer-name`;
const attributeTitleEmployerCity = `${attributeTitle}.employer-city`;
const attributeTitleEmployerPhone = `${attributeTitle}.employer-phone`;
const attributeTitleLocal = `${attributeTitle}.local`;
const attributeTitleAddressingData = `${attributeTitle}.addressing-data`;
const attributeFundsCode = `${attributeTitle}.funds-code`;
const attributeContentGiro = `${attributeContent}.giro`;
const attributeContentMaidenName = `${attributeTitle}.maiden-name`;

let holderName = '';
let coHolderName = '';

export const mapRecapCardList = (
  contractResponse: ContractResponse,
  referenceList: ReferenceDataStore,
  locale: string,
  translateService: TranslateService
): RecapCard[] => {
  holderName = formatPersonName(contractResponse.holder.physicalPerson);
  coHolderName = formatPersonName(contractResponse.coHolder?.physicalPerson);
  return [
    mapGeneralCard(
      contractResponse.vehicle,
      contractResponse.offer,
      contractResponse.externalSubStatus,
      referenceList.subStatus,
      translateService
    ),
    mapVehicleCard(contractResponse.vehicle, locale, translateService),
    mapFundingCard(contractResponse.offer, contractResponse.vehicle.annualMileage, locale, translateService),
    mapComplementaryProductCard(contractResponse.offer, contractResponse.insurances, locale, translateService),
    mapIdentityPhysicalPersonCard(
      contractResponse.holder,
      contractResponse.coHolder,
      referenceList.nationalities,
      referenceList.identityPieceType
    ),
    mapIdentityLegalEntityCard(contractResponse.holder, referenceList.nationalities, referenceList.identityPieceType),
    mapContactPhysicalPersonCard(contractResponse.holder, contractResponse.coHolder),
    mapContactLegalEntityCard(contractResponse.holder),
    mapBirthInformationPhysicalPersonCard(
      contractResponse.holder,
      contractResponse.coHolder,
      referenceList.countries,
      referenceList.metropolitanDepartments
    ),
    mapBirthInformationLegalEntityCard(
      contractResponse.holder,
      referenceList.countries,
      referenceList.metropolitanDepartments
    ),
    mapHousingPhysicalPersonCard(contractResponse.holder, contractResponse.coHolder, referenceList.habitation),
    mapHousingLegalEntityCard(contractResponse.holder, referenceList.habitation),
    mapFamilySituationPhysicalPersonCard(
      contractResponse.holder,
      contractResponse.coHolder,
      contractResponse.coHolderCode,
      referenceList.coholderType,
      referenceList.familySituation
    ),
    mapFamilySituationLegalEntityCard(contractResponse.holder, referenceList.familySituation),
    mapResourceCard(contractResponse.holder, contractResponse.coHolder, locale),
    mapExpenseCard(contractResponse.holder, contractResponse.coHolder, locale),
    mapProfessionalSituationCard(
      contractResponse.holder,
      contractResponse.coHolder,
      referenceList.countries,
      referenceList.professionCode,
      referenceList.contractCode,
      translateService
    ),
    mapEmployerCard(contractResponse.holder, contractResponse.coHolder, referenceList.countries),
    mapClientCompanyCard(contractResponse.holder, referenceList.juridicForms, locale, translateService),
    mapOldEmployerCard(contractResponse.holder, contractResponse.coHolder),
    mapCompanyCard(
      contractResponse.holder,
      referenceList.juridicForms,
      referenceList.countries,
      locale,
      translateService
    ),
    mapCompanyAddressCard(contractResponse.holder),
    mapAccountantCard(contractResponse.holder),
    mapAccountantPPHCard(contractResponse.holder, contractResponse.coHolder),
    mapBankCard(contractResponse.bankAccount, referenceList.bankType, translateService)
  ];
};

export const mapGeneralCard = (
  vehicle: VehicleResponse,
  offer: OfferResponse,
  externalSubStatus: string,
  subStatusReference: ReferenceValue[],
  translateService: TranslateService
): RecapCard => {
  return {
    title: `${cardTitle}.general`,
    iconName: 'binocular',
    holderAttributeList: [
      {
        title: `${attributeTitle}.expected-delivery-date`,
        content: formatLocaleDate(vehicle.deliveryDate),
        isModifiable: true,
        validators: [Validators.required],
        date: {
          minDate: new Date()
        }
      },
      {
        title: `${attributeTitle}.type-of-sale`,
        content: translateService.instant(
          `${attributeContent}.${offer.subscriptionMode === ESubscriptionMode.FAF ? 'face-to-face' : 'distance'}`
        )
      },
      {
        title: `${attributeTitle}.offer-date`,
        content: formatLocaleDate(offer.offerDate)
      },
      {
        title: `${attributeTitle}.purchase-order-number`,
        content: offer.purchaseOrderNumber
      },
      {
        title: `${attributeTitle}.use`,
        content: vehicle.professional
          ? translateService.instant(`${attributeContent}.professional`)
          : translateService.instant(`${attributeContent}.individual`)
      },
      {
        title: `${attributeTitle}.utilization`,
        content: getLeisureTypeLabel(vehicle.leisureType)
          ? translateService.instant(`${attributeContent}.${getLeisureTypeLabel(vehicle.leisureType)}`)
          : null
      },
      {
        title: `${attributeTitle}.sub-status`,
        content: getReferenceValue(externalSubStatus, subStatusReference)
      }
    ]
  };
};

export const mapVehicleCard = (
  vehicle: VehicleResponse,
  locale: string,
  translateService: TranslateService
): RecapCard => {
  return {
    title: `${cardTitle}.vehicle`,
    iconName: 'car',
    holderAttributeList: [
      {
        title: `${attributeTitle}.vo-vn`,
        content: translateService.instant(`${attributeContent}.${vehicle.used ? 'vo' : 'vn'}`)
      },
      {
        title: `${attributeTitle}.imported-vehicle`,
        content: translateService.instant(vehicle.imported ? attributeContentYes : attributeContentNo)
      },
      {
        title: `${attributeTitle}.vp-vu`,
        content: translateService.instant(`${attributeContent}.${vehicle.commercial ? 'vu' : 'vp'}`)
      },
      {
        title: `${attributeTitle}.first-mec`,
        content: formatLocaleDate(vehicle.firstRegistrationDate)
      },
      {
        title: `${attributeTitle}.vin-number`,
        content: vehicle.vin
      },
      {
        title: `${attributeTitle}.current-mileage`,
        content: formatValue(vehicle.currentMileage, RecapCardFormat.KM, locale)
      },
      {
        title: 'application.recap-card.attribute-title.registration',
        content:
          vehicle.registrationNumber && vehicle.imported !== null
            ? transformNonImportedRegistration(vehicle.registrationNumber, vehicle.imported)
            : null
      },
      {
        title: `${attributeTitle}.eligible-lease`,
        content: translateService.instant(vehicle.unbrokenVATChain ? attributeContentYes : attributeContentNo)
      },
      {
        title: `${attributeTitle}.brand`,
        content: vehicle.brand
      },
      {
        title: `${attributeTitle}.model`,
        content: vehicle.model
      },
      {
        title: `${attributeTitle}.version`,
        content: vehicle.version
      },
      {
        title: `${attributeTitle}.energy`,
        content: vehicle.energy
      },
      {
        title: `${attributeTitle}.horse-power`,
        content: formatValue(vehicle.horsePower, RecapCardFormat.NUMBER, locale)
      },
      {
        title: `${attributeTitle}.din-power`,
        content: formatValue(vehicle.dinHorsePower, RecapCardFormat.NUMBER, locale)
      },
      {
        title: `${attributeTitle}.catalog-price`,
        content: formatValue(vehicle.catalogPriceAmount, RecapCardFormat.EUROS, locale)
      },
      {
        title: `${attributeTitle}.purchase-price`,
        content: formatValue(vehicle.purchasePriceAmount, RecapCardFormat.EUROS, locale)
      },
      {
        title: `${attributeTitle}.options-amount`,
        content: formatValue(vehicle.optionsAmount, RecapCardFormat.EUROS, locale)
      },
      {
        title: `${attributeTitle}.accessories-amount`,
        content: formatValue(vehicle.accessoriesAmount, RecapCardFormat.EUROS, locale)
      }
    ]
  };
};

export const mapFundingCard = (
  offer: OfferResponse,
  annualMileage: number,
  locale: string,
  translateService: TranslateService
): RecapCard => {
  return {
    title: `${cardTitle}.funding`,
    iconName: 'financial-check',
    holderAttributeList: [
      {
        title: `${attributeTitle}.funding-type`,
        content: offer.product.productReference.commercialProduct
      },
      {
        title: `${attributeTitle}.duration`,
        content: translateService.instant(attributeContentMonthDuration, {
          months: offer.duration
        })
      },
      {
        title: `${attributeTitle}.scale`,
        content: `${offer.scaleCode}-${offer.pricingCode}`
      },
      {
        title: `${attributeTitle}.flex`,
        content: offer.product.flex ? attributeContentYes : null
      },
      {
        title: `${attributeTitle}.annual-mileage`,
        content: formatValue(annualMileage, RecapCardFormat.KM, locale)
      },
      {
        title: `${attributeTitle}.deposit-amount`,
        content: formatValue(offer.depositAmount, RecapCardFormat.EUROS, locale)
      },
      {
        title: `${attributeTitle}.security-deposit-amount`,
        content: formatValue(offer.securityDepositAmount, RecapCardFormat.EUROS, locale)
      },
      {
        title: `${attributeTitle}.overdraft`,
        content: formatValue(offer.overdraftAmount, RecapCardFormat.EUROS, locale)
      },
      {
        title: `${attributeTitle}.first-instalment-amount`,
        content: formatValue(offer.firstInstalmentAmount, RecapCardFormat.EUROS, locale)
      },
      {
        title: `${attributeTitle}.${offer.product?.code === 'C' || offer.product?.code === 'M' ? 'monthly-payment-without-insurance' : 'next-rent-without-insurance'}`,
        content: formatCurrency(offer.instalmentWithoutASMAmount, locale, '€')
      },
      {
        title: `${attributeTitle}.${offer.product?.code === 'C' || offer.product?.code === 'M' ? 'monthly-payment-with-insurance' : 'next-rent-with-insurance'}`,
        content: formatCurrency(offer.monthlyAmountWithInsurances, locale, '€')
      },
      {
        title: `${attributeTitle}.residual-value-amount`,
        content: formatValue(offer.residualValueAmount, RecapCardFormat.EUROS, locale)
      },
      {
        title: `${attributeTitle}.last-instalment-amount`,
        content: formatValue(offer.lastInstalmentAmount, RecapCardFormat.EUROS, locale)
      },
      {
        title: `${attributeTitle}.taea`,
        content: formatValue(offer.taea, RecapCardFormat.PERCENT, locale)
      },
      {
        title: `${attributeTitle}.taeg`,
        content: formatValue(offer.taeg, RecapCardFormat.PERCENT, locale)
      },
      {
        title: `${attributeTitle}.tnc`,
        content: formatValue(offer.teg, RecapCardFormat.PERCENT, locale)
      },
      {
        title: `${attributeTitle}.folder-fee`,
        content: formatValue(offer.folderFeesAmount, RecapCardFormat.EUROS, locale)
      }
    ]
  };
};

export const mapComplementaryProductCard = (
  offer: OfferResponse,
  insurances: InsuranceResponse[],
  locale: string,
  translateService: TranslateService
): RecapCard => {
  const recapCard: RecapCard = {
    title: `${cardTitle}.complementary-product`,
    iconName: 'customer',
    holderAttributeList: []
  };

  if (insurances?.length) {
    for (const insurance of insurances) {
      const insuranceLabel = getCorrectInsuranceLabel(insurance);

      recapCard.holderAttributeList.push(
        {
          title: attributeTitleProduct,
          content: insuranceLabel ? translateService.instant(insuranceLabel) : null
        },
        {
          title: `${attributeTitle}.option`,
          content: insurance.label
        },
        {
          title: `${attributeTitle}.monthly-price`,
          content: formatValue(insurance.instalmentAmount, RecapCardFormat.EUROS, locale)
        }
      );
    }
  }

  recapCard.importantAttribute = {
    title: `${attributeTitle}.insurances-monthly-amount`,
    content: formatValue(offer.insurancesMonthlyAmount, RecapCardFormat.EUROS, locale)
  };

  return recapCard;
};

export const mapIdentityPhysicalPersonCard = (
  holder: CreditHolderResponse,
  coHolder: CreditHolderResponse | NoValue,
  nationalities: ReferenceValue[],
  identityPieceType: ReferenceValue[]
): RecapCard => {
  const recapCard: RecapCard = {
    title: `${cardTitle}.identity`,
    iconName: 'customer',
    holderAttributeList: [
      {
        title: attributeTitleCivility,
        content: holder.physicalPerson?.civility
      },
      {
        title: attributeTitleFirstName,
        content: holder.physicalPerson?.firstName
      },
      {
        title: attributeTitleLastName,
        content: holder.physicalPerson?.lastName
      },
      {
        title: attributeContentMaidenName,
        content: holder.physicalPerson?.maidenName
      },
      {
        title: attributeTitleNationality,
        content: holder.physicalPerson?.nationality
          ? getReferenceValue(holder.physicalPerson?.nationality, nationalities)
          : null
      },
      {
        title: attributeTitleNumber,
        content: holder.physicalPerson?.identityPiece.identifier
      },
      {
        title: attributeTitleIdentityPieceIdentifier,
        content: holder.physicalPerson?.identityPiece?.type
          ? getReferenceValue(holder.physicalPerson?.identityPiece?.type, identityPieceType)
          : null
      }
    ]
  };

  if (coHolder?.physicalPerson) {
    recapCard.coHolderAttributeList = [
      {
        title: attributeTitleCivility,
        content: coHolder.physicalPerson.civility
      },
      {
        title: attributeTitleFirstName,
        content: coHolder.physicalPerson.firstName
      },
      {
        title: attributeTitleLastName,
        content: coHolder.physicalPerson.lastName
      },
      {
        title: attributeContentMaidenName,
        content: coHolder.physicalPerson.maidenName
      },
      {
        title: attributeTitleNationality,
        content: getReferenceValue(coHolder.physicalPerson.nationality, nationalities)
      },
      {
        title: attributeTitleNumber,
        content: coHolder.physicalPerson.identityPiece.identifier
      },
      {
        title: attributeTitleIdentityPieceIdentifier,
        content: getReferenceValue(coHolder.physicalPerson.identityPiece.type, identityPieceType)
      }
    ];
    recapCard.holderName = holderName;
    recapCard.coholderName = coHolderName;
  }

  return recapCard;
};

export const mapIdentityLegalEntityCard = (
  holder: CreditHolderResponse,
  nationalities: ReferenceValue[],
  identityPieceType: ReferenceValue[]
): RecapCard => {
  const recapCard: RecapCard = {
    title: `${cardTitle}.identity`,
    iconName: 'customer',
    holderAttributeList: []
  };

  if (holder.legalEntity) {
    recapCard.holderAttributeList.push(
      {
        title: attributeTitleCivility,
        content: holder.legalEntity.legalRepresentative.civility
      },
      {
        title: attributeTitleFirstName,
        content: holder.legalEntity.legalRepresentative.firstName
      },
      {
        title: attributeTitleLastName,
        content: holder.legalEntity.legalRepresentative.lastName
      },
      {
        title: attributeTitleLastName,
        content: holder.legalEntity.legalRepresentative.maidenName
      },
      {
        title: attributeTitleNationality,
        content: getReferenceValue(holder.legalEntity.legalRepresentative.nationality, nationalities)
      },
      {
        title: attributeTitleNumber,
        content: holder.legalEntity.legalRepresentative.identityPiece.identifier
      },
      {
        title: attributeTitleIdentityPieceIdentifier,
        content: getReferenceValue(holder.legalEntity.legalRepresentative.identityPiece.type, identityPieceType)
      }
    );
  }

  return recapCard;
};

export const mapContactPhysicalPersonCard = (
  holder: CreditHolderResponse,
  coHolder: CreditHolderResponse | NoValue
): RecapCard => {
  const recapCard: RecapCard = {
    title: `${cardTitle}.contact`,
    iconName: 'telephone',
    holderAttributeList: []
  };

  if (holder.physicalPerson) {
    recapCard.holderAttributeList.push(
      {
        title: attributeTitleEmailAddress,
        content: holder.physicalPerson.mail
      },
      {
        title: attributeTitleCellPhoneNumber,
        content: holder.physicalPerson.cellPhone
      }
    );
  }

  if (coHolder?.physicalPerson) {
    recapCard.coHolderAttributeList = [
      {
        title: attributeTitleEmailAddress,
        content: coHolder.physicalPerson.mail
      },
      {
        title: attributeTitleCellPhoneNumber,
        content: coHolder.physicalPerson.cellPhone
      }
    ];
    recapCard.holderName = holderName;
    recapCard.coholderName = coHolderName;
  }

  return recapCard;
};

export const mapContactLegalEntityCard = (holder: CreditHolderResponse): RecapCard => {
  const recapCard: RecapCard = {
    title: `${cardTitle}.contact`,
    iconName: 'telephone',
    holderAttributeList: []
  };

  if (holder.legalEntity) {
    recapCard.holderAttributeList.push(
      {
        title: attributeTitleEmailAddress,
        content: holder.legalEntity.legalRepresentative.mail
      },
      {
        title: attributeTitleCellPhoneNumber,
        content: holder.legalEntity.legalRepresentative.cellPhone
      }
    );
  }

  return recapCard;
};

export const mapBirthInformationPhysicalPersonCard = (
  holder: CreditHolderResponse,
  coHolder: CreditHolderResponse | NoValue,
  countries: ReferenceValue[],
  metropolitanDepartments: ReferenceValue[]
): RecapCard => {
  const recapCard: RecapCard = {
    title: `${cardTitle}.birth-information`,
    iconName: 'calendar',
    holderAttributeList: [
      {
        title: attributeTitleBirthCountry,
        content: holder.physicalPerson?.originCountry
          ? getReferenceValue(holder.physicalPerson?.originCountry, countries)
          : null
      },
      {
        title: attributeTitleBirthDate,
        content: formatLocaleDate(holder.physicalPerson?.birthDate)
      },
      {
        title: attributeTitleBirthDepartment,
        content: holder.physicalPerson?.birthDepartment
          ? getReferenceValue(holder.physicalPerson?.birthDepartment, metropolitanDepartments)
          : null
      },
      {
        title: attributeTitleBirthCity,
        content: holder.physicalPerson?.birthCity
      }
    ]
  };

  if (coHolder?.physicalPerson) {
    recapCard.coHolderAttributeList = [
      {
        title: attributeTitleBirthCountry,
        content: getReferenceValue(coHolder.physicalPerson.originCountry, countries)
      },
      {
        title: attributeTitleBirthDate,
        content: formatLocaleDate(coHolder.physicalPerson.birthDate)
      },
      {
        title: attributeTitleBirthDepartment,
        content: getReferenceValue(coHolder.physicalPerson.birthDepartment, metropolitanDepartments)
      },
      {
        title: attributeTitleBirthCity,
        content: coHolder.physicalPerson.birthCity
      }
    ];
    recapCard.holderName = holderName;
    recapCard.coholderName = coHolderName;
  }

  return recapCard;
};

export const mapBirthInformationLegalEntityCard = (
  holder: CreditHolderResponse,
  countries: ReferenceValue[],
  metropolitanDepartments: ReferenceValue[]
): RecapCard => {
  const recapCard: RecapCard = {
    title: `${cardTitle}.birth-information`,
    iconName: 'calendar',
    holderAttributeList: []
  };

  if (holder.legalEntity) {
    recapCard.holderAttributeList.push(
      {
        title: attributeTitleBirthCountry,
        content: getReferenceValue(holder.legalEntity.legalRepresentative.originCountry, countries)
      },
      {
        title: attributeTitleBirthDate,
        content: formatLocaleDate(holder.legalEntity.legalRepresentative.birthDate)
      },
      {
        title: attributeTitleBirthDepartment,
        content: getReferenceValue(holder.legalEntity.legalRepresentative.birthDepartment, metropolitanDepartments)
      },
      {
        title: attributeTitleBirthCity,
        content: holder.legalEntity.legalRepresentative.birthCity
      }
    );
  }

  return recapCard;
};

export const mapHousingPhysicalPersonCard = (
  holder: CreditHolderResponse,
  coHolder: CreditHolderResponse | NoValue,
  habitations: ReferenceValue[]
): RecapCard => {
  const recapCard: RecapCard = {
    title: `${cardTitle}.housing`,
    iconName: 'home',
    holderAttributeList: []
  };

  if (holder.physicalPerson) {
    recapCard.holderAttributeList.push(
      {
        title: attributeTitleAddress,
        content: holder.physicalPerson.address.street
      },
      {
        title: attributeTitleAdditionalAddress,
        content: holder.physicalPerson.address.additionalInfo
      },
      {
        title: attributeTitlePostCode,
        content: holder.physicalPerson.address.postCode
      },
      {
        title: attributeTitleCity,
        content: holder.physicalPerson.address.city
      },
      {
        title: attributeTitleHousingMode,
        content: getReferenceValue(holder.physicalPerson.address.habitationCode, habitations)
      },
      {
        title: attributeTitleSince,
        content: formatLocaleDateMonthYear(holder.physicalPerson.address.entryDate)
      }
    );
  }

  if (coHolder?.physicalPerson) {
    recapCard.coHolderAttributeList = [
      {
        title: attributeTitleAddress,
        content: coHolder.physicalPerson.address.street
      },
      {
        title: attributeTitleAdditionalAddress,
        content: coHolder.physicalPerson.address.additionalInfo
      },
      {
        title: attributeTitlePostCode,
        content: coHolder.physicalPerson.address.postCode
      },
      {
        title: attributeTitleCity,
        content: coHolder.physicalPerson.address.city
      },
      {
        title: attributeTitleHousingMode,
        content: getReferenceValue(coHolder.physicalPerson.address.habitationCode, habitations)
      },
      {
        title: attributeTitleSince,
        content: formatLocaleDateMonthYear(coHolder.physicalPerson.address.entryDate)
      }
    ];
  }

  return recapCard;
};

export const mapHousingLegalEntityCard = (holder: CreditHolderResponse, habitations: ReferenceValue[]): RecapCard => {
  const recapCard: RecapCard = {
    title: `${cardTitle}.housing`,
    iconName: 'home',
    holderAttributeList: []
  };

  if (holder.legalEntity) {
    recapCard.holderAttributeList.push(
      {
        title: attributeTitleAddress,
        content: holder.legalEntity.legalRepresentative.address.street
      },
      {
        title: attributeTitleAdditionalAddress,
        content: holder.legalEntity.legalRepresentative.address.additionalInfo
      },
      {
        title: attributeTitlePostCode,
        content: holder.legalEntity.legalRepresentative.address.postCode
      },
      {
        title: attributeTitleCity,
        content: holder.legalEntity.legalRepresentative.address.city
      },
      {
        title: attributeTitleHousingMode,
        content: getReferenceValue(holder.legalEntity.legalRepresentative.address.habitationCode, habitations)
      },
      {
        title: attributeTitleSince,
        content: formatLocaleDateMonthYear(new Date(holder.legalEntity.legalRepresentative.address.entryDate))
      }
    );
  }

  return recapCard;
};

export const mapFamilySituationPhysicalPersonCard = (
  holder: CreditHolderResponse,
  coHolder: CreditHolderResponse | NoValue,
  coHolderCode: string | NoValue,
  coHolderType: ReferenceValue[],
  familySituation: ReferenceValue[]
): RecapCard => {
  const recapCard: RecapCard = {
    title: `${cardTitle}.family-situation`,
    iconName: 'three-people',
    holderAttributeList: []
  };

  if (holder.physicalPerson) {
    if (coHolderCode) {
      recapCard.holderAttributeList.push({
        title: `${attributeTitle}.coholder-type`,
        content: getReferenceValue(coHolderCode, coHolderType)
      });
    }

    recapCard.holderAttributeList.push(
      {
        title: attributeTitleSituation,
        content: getReferenceValue(holder.physicalPerson.familySituation.familySituationCode, familySituation)
      },
      {
        title: attributeTitleDependentChildrenNumber,
        content: holder.physicalPerson.familySituation.childCount?.toString() ?? null
      },
      {
        title: attributeTitleMatrimonialRegime,
        content: getMatrimonialStatusLabel(holder.physicalPerson.familySituation.matrimonialStatus as MatrimonialStatus)
      }
    );
  }

  if (coHolder?.physicalPerson) {
    recapCard.coHolderAttributeList = [
      {
        title: attributeTitleSituation,
        content: getReferenceValue(coHolder.physicalPerson.familySituation.familySituationCode, familySituation)
      },
      {
        title: attributeTitleDependentChildrenNumber,
        content: coHolder.physicalPerson.familySituation.childCount?.toString() ?? null
      },
      {
        title: attributeTitleMatrimonialRegime,
        content: getMatrimonialStatusLabel(
          coHolder.physicalPerson.familySituation.matrimonialStatus as MatrimonialStatus
        )
      }
    ];
    recapCard.holderName = holderName;
    recapCard.coholderName = coHolderName;
  }

  return recapCard;
};

export const mapFamilySituationLegalEntityCard = (
  holder: CreditHolderResponse,
  familySituation: ReferenceValue[]
): RecapCard => {
  const recapCard: RecapCard = {
    title: `${cardTitle}.family-situation`,
    iconName: 'three-people',
    holderAttributeList: []
  };

  if (holder.legalEntity) {
    recapCard.holderAttributeList.push(
      {
        title: attributeTitleSituation,
        content: getReferenceValue(
          holder.legalEntity.legalRepresentative.familySituation.familySituationCode,
          familySituation
        )
      },
      {
        title: attributeTitleDependentChildrenNumber,
        content: holder.legalEntity.legalRepresentative.familySituation.childCount?.toString() ?? null
      },
      {
        title: attributeTitleMatrimonialRegime,
        content: getMatrimonialStatusLabel(
          holder.legalEntity.legalRepresentative.familySituation.matrimonialStatus as MatrimonialStatus
        )
      }
    );
  }

  return recapCard;
};

export const mapResourceCard = (
  holder: CreditHolderResponse,
  coHolder: CreditHolderResponse | NoValue,
  locale: string
): RecapCard => {
  const recapCard: RecapCard = {
    title: `${cardTitle}.resources`,
    iconName: 'piggy-bank',
    holderAttributeList: []
  };

  if (holder.physicalPerson) {
    recapCard.holderAttributeList.push(
      {
        title: attributeTitleMonthlyNetIncome,
        content: formatCurrency(holder.physicalPerson.budget.salaryAmount ?? 0, locale, '€', 'EUR', '.2')
      },
      {
        title: attributeTitleSocialBenefits,
        content: formatCurrency(holder.physicalPerson.budget.socialBenefitCreditAmount ?? 0, locale, '€', 'EUR', '.2')
      },
      {
        title: attributeTitleOtherIncomes,
        content: formatCurrency(holder.physicalPerson.budget.otherIncomesAmount ?? 0, locale, '€', 'EUR', '.2')
      }
    );
  }

  if (coHolder?.physicalPerson) {
    recapCard.coHolderAttributeList = [
      {
        title: attributeTitleMonthlyNetIncome,
        content: formatCurrency(coHolder.physicalPerson.budget.salaryAmount ?? 0, locale, '€', 'EUR', '.2')
      },
      {
        title: attributeTitleSocialBenefits,
        content: formatCurrency(coHolder.physicalPerson.budget.socialBenefitCreditAmount ?? 0, locale, '€', 'EUR', '.2')
      },
      {
        title: attributeTitleOtherIncomes,
        content: formatCurrency(coHolder.physicalPerson.budget.otherIncomesAmount ?? 0, locale, '€', 'EUR', '.2')
      }
    ];
    recapCard.holderName = holderName;
    recapCard.coholderName = coHolderName;
  }

  return recapCard;
};

export const mapExpenseCard = (
  holder: CreditHolderResponse,
  coHolder: CreditHolderResponse | NoValue,
  locale: string
): RecapCard => {
  const recapCard: RecapCard = {
    title: `${cardTitle}.expenses`,
    iconName: 'cycling-payment',
    holderAttributeList: []
  };

  if (holder.physicalPerson) {
    recapCard.holderAttributeList.push(
      {
        title: attributeTitleHousingExpense,
        content: formatValue(holder.physicalPerson.budget.principalRentAmount, RecapCardFormat.EUROS, locale)
      },
      {
        title: attributeTitleAlimonyPaid,
        content: formatValue(holder.physicalPerson.budget.childSupportAmount, RecapCardFormat.EUROS, locale)
      },
      {
        title: attributeTitleOtherExpenses,
        content: formatValue(holder.physicalPerson.budget.autoCreditAmount, RecapCardFormat.EUROS, locale)
      }
    );
  }

  if (coHolder?.physicalPerson) {
    recapCard.coHolderAttributeList = [
      {
        title: attributeTitleHousingExpense,
        content: formatValue(coHolder.physicalPerson.budget.principalRentAmount, RecapCardFormat.EUROS, locale)
      },
      {
        title: attributeTitleAlimonyPaid,
        content: formatValue(coHolder.physicalPerson.budget.childSupportAmount, RecapCardFormat.EUROS, locale)
      },
      {
        title: attributeTitleOtherExpenses,
        content: formatValue(coHolder.physicalPerson.budget.autoCreditAmount, RecapCardFormat.EUROS, locale)
      }
    ];
    recapCard.holderName = holderName;
    recapCard.coholderName = coHolderName;
  }
  return recapCard;
};

export const mapProfessionalSituationCard = (
  holder: CreditHolderResponse,
  coHolder: CreditHolderResponse | NoValue,
  countries: ReferenceValue[],
  professionCode: ReferenceValue[],
  contractCode: ReferenceValue[],
  translateService: TranslateService
): RecapCard => {
  const recapCard: RecapCard = {
    title: `${cardTitle}.professional-situation`,
    iconName: 'travelling',
    holderAttributeList: []
  };

  if (holder.physicalPerson) {
    recapCard.holderAttributeList.push(
      {
        title: attributeTitleActivitySector,
        content: translateService.instant(`${attributeContentGiro}.${holder.physicalPerson.profession.giro}`)
      },
      {
        title: attributeTitleJobSituation,
        content:
          professionCode.find(item => item.key === holder.physicalPerson?.profession.professionCode)?.value ??
          holder.physicalPerson?.profession.professionCode
      },
      {
        title: attributeTitleSince,
        content: formatLocaleDateMonthYear(holder.physicalPerson.profession.employmentStartingDate)
      },
      {
        title: attributeTitleContractType,
        content:
          contractCode.find(item => item.key === holder.physicalPerson?.profession.employment.contractCode)?.value ??
          holder.physicalPerson?.profession.employment.contractCode
      },
      {
        title: attributeTitleQualification,
        content: holder.physicalPerson.profession.qualification
      },
      {
        title: attributeTitleMainActivityCountry,
        content: getReferenceValue(holder.physicalPerson.profession.kycActivityCountry, countries)
      }
    );
  }

  if (coHolder?.physicalPerson) {
    recapCard.coHolderAttributeList = [
      {
        title: attributeTitleActivitySector,
        content: translateService.instant(`${attributeContentGiro}.${coHolder.physicalPerson.profession.giro}`)
      },
      {
        title: attributeTitleJobSituation,
        content:
          professionCode.find(item => item.key === coHolder.physicalPerson?.profession.professionCode)?.value ??
          coHolder.physicalPerson.profession.professionCode
      },
      {
        title: attributeTitleSince,
        content: formatLocaleDateMonthYear(coHolder.physicalPerson.profession.employmentStartingDate)
      },
      {
        title: attributeTitleContractType,
        content:
          contractCode.find(item => item.key === holder.physicalPerson?.profession.employment.contractCode)?.value ??
          coHolder.physicalPerson.profession.employment.contractCode
      },
      {
        title: attributeTitleQualification,
        content: coHolder.physicalPerson.profession.qualification
      },
      {
        title: attributeTitleMainActivityCountry,
        content: getReferenceValue(coHolder.physicalPerson.profession.kycActivityCountry, countries)
      }
    ];
    recapCard.holderName = holderName;
    recapCard.coholderName = coHolderName;
  }
  return recapCard;
};

export const mapEmployerCard = (
  holder: CreditHolderResponse,
  coHolder: CreditHolderResponse | NoValue,
  countries: ReferenceValue[]
): RecapCard => {
  const recapCard: RecapCard = {
    title: `${cardTitle}.employer`,
    iconName: 'magnifying-glass-2',
    holderAttributeList: []
  };
  if (
    holder?.physicalPerson?.profession.professionCode === '10' ||
    holder?.physicalPerson?.profession.professionCode === '15' ||
    holder?.physicalPerson?.profession.professionCode === '97'
  ) {
    recapCard.holderAttributeList.push(
      {
        title: attributeTitleLastName,
        content: holder.physicalPerson.profession.employment.companyInfo.name
      },
      {
        title: attributeTitleActivity,
        content: holder.physicalPerson.profession.employment.activity
      },
      {
        title: attributeTitleAddress,
        content: holder.physicalPerson.profession.employment.companyInfo.address.street
      },
      {
        title: attributeTitleComplementary,
        content: holder.physicalPerson.profession.employment.companyInfo.address.additionalInfo
      },
      {
        title: attributeTitlePostCode,
        content: holder.physicalPerson.profession.employment.companyInfo.address.postCode
      },
      {
        title: attributeTitleCity,
        content: holder.physicalPerson.profession.employment.companyInfo.address.city
      },
      {
        title: attributeTitleQualification,
        content: holder.physicalPerson.profession.qualification
      },
      {
        title: attributeTitleCountry,
        content: getReferenceValue(
          holder.physicalPerson?.profession?.employment?.companyInfo?.address?.country,
          countries
        )
      },
      {
        title: attributeTitlePhone,
        content: holder.physicalPerson.profession.employment.companyInfo.address.phone
      }
    );
  } else if (holder.physicalPerson?.profession.employer.address) {
    recapCard.holderAttributeList.push(
      {
        title: attributeTitleEmployerName,
        content: holder.physicalPerson.profession.employment.companyInfo.name
      },
      {
        title: attributeTitleAddress,
        content: holder.physicalPerson.profession.employer.address.street
      },
      {
        title: attributeTitlePostCode,
        content: holder.physicalPerson.profession.employer.address.postCode
      },
      {
        title: attributeTitleEmployerCity,
        content: holder.physicalPerson.profession.employer.address.city
      },
      {
        title: attributeTitleEmployerPhone,
        content: holder.physicalPerson.profession.employer.address.phone
      }
    );
  }

  if (
    coHolder?.physicalPerson?.profession.professionCode === '10' ||
    coHolder?.physicalPerson?.profession.professionCode === '15' ||
    coHolder?.physicalPerson?.profession.professionCode === '97'
  ) {
    recapCard.coHolderAttributeList = [
      {
        title: attributeTitleLastName,
        content: coHolder.physicalPerson.profession.employment.companyInfo.name
      },
      {
        title: attributeTitleActivity,
        content: coHolder.physicalPerson.profession.employment.activity
      },
      {
        title: attributeTitleAddress,
        content: coHolder.physicalPerson.profession.employment.companyInfo.address.street
      },
      {
        title: attributeTitleComplementary,
        content: coHolder.physicalPerson.profession.employment.companyInfo.address.additionalInfo
      },
      {
        title: attributeTitlePostCode,
        content: coHolder.physicalPerson.profession.employment.companyInfo.address.postCode
      },
      {
        title: attributeTitleCity,
        content: coHolder.physicalPerson.profession.employment.companyInfo.address.city
      },
      {
        title: attributeTitleQualification,
        content: coHolder.physicalPerson.profession.qualification
      },
      {
        title: attributeTitleCountry,
        content: getReferenceValue(
          coHolder.physicalPerson?.profession?.employment?.companyInfo?.address?.country,
          countries
        )
      },
      {
        title: attributeTitlePhone,
        content: coHolder.physicalPerson.profession.employment.companyInfo.address.phone
      }
    ];
    recapCard.holderName = holderName;
    recapCard.coholderName = coHolderName;
  } else if (coHolder?.physicalPerson?.profession.employer.address) {
    recapCard.coHolderAttributeList = [
      {
        title: attributeTitleEmployerName,
        content: coHolder.physicalPerson.profession.employment.companyInfo.name
      },
      {
        title: attributeTitleAddress,
        content: coHolder.physicalPerson.profession.employer.address.street
      },
      {
        title: attributeTitlePostCode,
        content: coHolder.physicalPerson.profession.employer.address.postCode
      },
      {
        title: attributeTitleEmployerCity,
        content: coHolder.physicalPerson.profession.employer.address.city
      },
      {
        title: attributeTitleEmployerPhone,
        content: coHolder.physicalPerson.profession.employer.address.phone
      }
    ];
    recapCard.holderName = holderName;
    recapCard.coholderName = coHolderName;
  }

  return recapCard;
};

export const mapClientCompanyCard = (
  holder: CreditHolderResponse,
  juridicForms: ReferenceValue[],
  locale: string,
  translateService: TranslateService
): RecapCard => {
  const recapCard: RecapCard = {
    title: 'application.recap-card.card-title.client-company',
    iconName: 'suit-case',
    holderAttributeList: []
  };
  const profession = holder.physicalPerson?.profession;

  if (
    profession &&
    ((isInEnum(profession.giro, ActivityProSiren) && !isInEnum(profession.professionCode, ProfessionNoSiren)) ||
      isInEnum(profession.professionCode, ProfessionCompany))
  ) {
    recapCard.holderAttributeList.push(
      {
        title: attributeTitleSiren,
        content: profession.employerInfo.companyInfo.siren
      },
      {
        title: attributeTitleCreationDate,
        content: formatLocaleDate(profession.employer.creationDate)
      },
      {
        title: attributeTitleLegalForm,
        content: getReferenceValue(profession.employer.legalForm, juridicForms)
      },
      {
        title: attributeTitleAdminCourt,
        content: profession.employer.adminCourtService
      },
      {
        title: attributeTitleCodeNaf,
        content: profession.employer.apeCode
      },
      {
        title: attributeTitleCapital,
        content: formatValue(profession.employer.capitalAmount, RecapCardFormat.EUROS, locale)
      },
      {
        title: attributeTitleAddressingData,
        content: addressingDtoToString(profession.employer.addressing)
      }
    );

    if (profession.employer.fundsCode === 'L' || profession.employer.fundsCode === 'P') {
      recapCard.holderAttributeList.push({
        title: attributeFundsCode,
        content: translateService.instant(
          profession.employer.fundsCode === 'L' ? attributeContentTenant : attributeContentOwner
        )
      });
    }
    if (profession.employer.mursCode === 'L' || profession.employer.mursCode === 'P') {
      recapCard.holderAttributeList.push({
        title: `${attributeTitle}.local`,
        content: translateService.instant(
          profession.employer.mursCode === 'L' ? attributeContentTenant : attributeContentOwner
        )
      });
    }

    recapCard.holderAttributeList.push({
      title: attributeTitleEmployeeNumber,
      content: profession.employer.numberOfEmployees?.toString() ?? null
    });
  }

  return recapCard;
};

export const mapOldEmployerCard = (
  holder: CreditHolderResponse,
  coHolder: CreditHolderResponse | NoValue
): RecapCard => {
  const recapCard: RecapCard = {
    title: `${cardTitle}.old-employer`,
    iconName: 'agency-branches',
    holderAttributeList: []
  };

  if (holder.physicalPerson) {
    const duration = holder.physicalPerson.profession.previousEmployment?.duration;
    recapCard.holderAttributeList.push(
      {
        title: attributeTitleLastName,
        content: holder.physicalPerson.profession.previousEmployment?.companyInfo?.name
      },
      {
        title: attributeTitleSeniority,
        content: duration?.toString() ?? null
      }
    );
  }

  if (coHolder?.physicalPerson) {
    const duration = coHolder.physicalPerson.profession.previousEmployment?.duration;
    recapCard.coHolderAttributeList = [
      {
        title: attributeTitleLastName,
        content: coHolder.physicalPerson.profession.previousEmployment?.companyInfo?.name
      },
      {
        title: attributeTitleSeniority,
        content: duration?.toString() ?? null
      }
    ];
    recapCard.holderName = holderName;
    recapCard.coholderName = coHolderName;
  }

  return recapCard;
};

export const mapCompanyCard = (
  holder: CreditHolderResponse,
  juridicForms: ReferenceValue[],
  countries: ReferenceValue[],
  locale: string,
  translateService: TranslateService
): RecapCard => {
  const recapCard: RecapCard = {
    title: `${cardTitle}.company`,
    iconName: 'suit-case',
    holderAttributeList: []
  };

  if (holder.legalEntity?.company) {
    const company = holder.legalEntity?.company;

    recapCard.holderAttributeList.push(
      {
        title: attributeTitleSiren,
        content: company.siren
      },
      {
        title: attributeTitleLastName,
        content: company.name
      },
      {
        title: attributeTitleActivity,
        content: company.activity
      },
      {
        title: attributeTitleCreationDate,
        content: formatLocaleDate(company.creationDate)
      },
      {
        title: attributeTitleLegalForm,
        content: getReferenceValue(company.legalForm, juridicForms)
      },
      {
        title: attributeTitleAdminCourt,
        content: company.adminCourtService
      },
      {
        title: attributeTitleCodeNaf,
        content: company.apeCode
      },
      {
        title: attributeTitleCapital,
        content: formatValue(company.capitalAmount, RecapCardFormat.EUROS, locale)
      },
      {
        title: attributeTitleAddressingData,
        content: addressingDtoToString(company.addressing)
      }
    );

    if (holder.legalEntity?.company.fundsCode === 'L') {
      recapCard.holderAttributeList.push({
        title: attributeFundsCode,
        content: translateService.instant(attributeContentTenant)
      });
    } else if (holder.legalEntity?.company.fundsCode === 'P') {
      recapCard.holderAttributeList.push({
        title: attributeFundsCode,
        content: translateService.instant(attributeContentOwner)
      });
    }

    if (holder.legalEntity?.company.mursCode === 'L') {
      recapCard.holderAttributeList.push({
        title: attributeTitleLocal,
        content: translateService.instant(attributeContentTenant)
      });
    } else if (holder.legalEntity?.company.mursCode === 'P') {
      recapCard.holderAttributeList.push({
        title: attributeTitleLocal,
        content: translateService.instant(attributeContentOwner)
      });
    }

    recapCard.holderAttributeList.push(
      {
        title: attributeTitleEmployeeNumber,
        content: formatValue(holder.legalEntity?.company.numberOfEmployees, RecapCardFormat.NUMBER, locale)
      },
      {
        title: `${attributeTitle}.email`,
        content: holder.legalEntity?.company.email
      },
      {
        title: attributeTitleMainActivityCountry,
        content: getReferenceValue(holder.legalEntity?.kyc.activityCountry, countries)
      },
      {
        title: `${attributeTitle}.presence-investment`,
        content: translateService.instant(
          holder.legalEntity?.kyc.secondQuestion ? attributeContentYes : attributeContentNo
        )
      },
      {
        title: `${attributeTitle}.operation-activity`,
        content: translateService.instant(
          holder.legalEntity?.kyc.thirdQuestion ? attributeContentYes : attributeContentNo
        )
      },
      {
        title: `${attributeTitle}.signatory-status`,
        content: holder.legalEntity?.legalRepresentative.profession.qualification
      },
      {
        title: `${attributeTitle}.deposit`,
        content: translateService.instant(
          holder.legalEntity?.legalRepresentativeCaution ? attributeContentYes : attributeContentNo
        )
      }
    );
  }

  return recapCard;
};

export const mapCompanyAddressCard = (holder: CreditHolderResponse): RecapCard => {
  const recapCard: RecapCard = {
    title: `${cardTitle}.company-address`,
    iconName: 'map',
    holderAttributeList: []
  };

  if (holder.legalEntity?.company) {
    recapCard.holderAttributeList.push(
      {
        title: attributeTitleAddress,
        content: holder.legalEntity.company.address.street
      },
      {
        title: attributeTitleComplementary,
        content: holder.legalEntity.company.address.additionalInfo
      },
      {
        title: attributeTitlePostCode,
        content: holder.legalEntity.company.address.postCode
      },
      {
        title: attributeTitleCity,
        content: holder.legalEntity.company.address.city
      },
      {
        title: attributeTitlePhone,
        content: holder.legalEntity.company.address.phone
      }
    );
  }

  return recapCard;
};

export const mapAccountantCard = (holder: CreditHolderResponse): RecapCard => {
  const recapCard: RecapCard = {
    title: 'application.recap-card.card-title.accountant',
    iconName: 'cashier',
    holderAttributeList: []
  };

  if (holder.legalEntity?.accountant) {
    recapCard.holderAttributeList.push(
      {
        title: attributeTitleLastName,
        content: holder.legalEntity.accountant.name
      },
      {
        title: attributeTitleAddress,
        content: holder.legalEntity.accountant.address.street
      },
      {
        title: attributeTitleComplementary,
        content: holder.legalEntity.accountant.address.additionalInfo
      },
      {
        title: attributeTitlePostCode,
        content: holder.legalEntity.accountant.address.postCode
      },
      {
        title: attributeTitleCity,
        content: holder.legalEntity.accountant.address.city
      },
      {
        title: attributeTitlePhone,
        content: holder.legalEntity.accountant.address.phone
      }
    );
  }

  return recapCard;
};

export const mapAccountantPPHCard = (
  holder: CreditHolderResponse,
  coHolder: CreditHolderResponse | NoValue
): RecapCard => {
  const recapCard: RecapCard = {
    title: 'application.recap-card.card-title.accountant',
    iconName: 'cashier',
    holderAttributeList: []
  };

  if (isInEnum(holder?.physicalPerson?.profession.professionCode, ProfessionCompany)) {
    recapCard.holderAttributeList.push(
      {
        title: attributeTitleLastName,
        content: holder.physicalPerson?.profession.employer.complementaryName
      },
      {
        title: attributeTitleAddress,
        content: holder.physicalPerson?.profession.employer.address.street
      },
      {
        title: attributeTitleComplementary,
        content: holder.physicalPerson?.profession.employer.address.additionalInfo
      },
      {
        title: attributeTitlePostCode,
        content: holder.physicalPerson?.profession.employer.address.postCode
      },
      {
        title: attributeTitleCity,
        content: holder.physicalPerson?.profession.employer.address.city
      },
      {
        title: attributeTitlePhone,
        content: holder.physicalPerson?.profession.employer.address.phone
      }
    );
  }

  if (
    coHolder?.physicalPerson?.profession.professionCode === '10' ||
    coHolder?.physicalPerson?.profession.professionCode === '15' ||
    coHolder?.physicalPerson?.profession.professionCode === '97'
  ) {
    recapCard.coHolderAttributeList = [
      {
        title: attributeTitleLastName,
        content: coHolder.physicalPerson?.profession.employer.complementaryName
      },
      {
        title: attributeTitleAddress,
        content: coHolder.physicalPerson?.profession.employer.address.street
      },
      {
        title: attributeTitleComplementary,
        content: coHolder.physicalPerson?.profession.employer.address.additionalInfo
      },
      {
        title: attributeTitlePostCode,
        content: coHolder.physicalPerson?.profession.employer.address.postCode
      },
      {
        title: attributeTitleCity,
        content: coHolder.physicalPerson?.profession.employer.address.city
      },
      {
        title: attributeTitlePhone,
        content: coHolder.physicalPerson?.profession.employer.address.phone
      }
    ];
    recapCard.holderName = holderName;
    recapCard.coholderName = coHolderName;
  }
  return recapCard;
};

export const mapBankCard = (
  bankAccount: BankAccountResponse,
  bankType: ReferenceValue[],
  translateService: TranslateService
): RecapCard => {
  return {
    title: `${cardTitle}.bank`,
    iconName: 'bank',
    holderAttributeList: [
      {
        title: `${attributeTitle}.opening-date`,
        content: formatLocaleDateMonthYear(bankAccount.accountOpeningDate)
      },
      {
        title: `${attributeTitle}.bank`,
        content: getReferenceValue(bankAccount.bankCode, bankType)
      },
      {
        title: `${attributeTitle}.account-holder`,
        content: translateService.instant(getBankAccountContent(bankAccount.accountCode as AccountOwner))
      },
      {
        title: `${attributeTitle}.bic`,
        content: bankAccount.bic
      },
      {
        title: `${attributeTitle}.iban`,
        content: bankAccount.iban?.replaceAll(/.{4}(?!$)/g, match => match + ' ') ?? null
      },
      {
        title: `${attributeTitle}.levy-day`,
        content: translateService.instant(`${attributeContent}.day-of-one-month`, {
          day: bankAccount.levyDay
        })
      }
    ]
  };
};

export const getLeisureTypeLabel = (leisureType: string): string | null => {
  switch (leisureType) {
    case 'T':
      return 'tourism';
    case 'H':
      return 'dwelling';
    case 'P':
      return 'professional';
    default:
      return null;
  }
};

export const getCorrectInsuranceLabel = (insurance: InsuranceResponse | NoValue): string | null => {
  switch (insurance?.category) {
    case 'ASM':
      if (insurance.carrierType === 'T') {
        return `${attributeContent}.insurance-holder`;
      } else if (insurance.carrierType === 'C') {
        return `${attributeContent}.insurance-coholder`;
      } else if (insurance.carrierType === null || insurance.carrierType === undefined) {
        return `${attributeContent}.insurance-holder-coholder`;
      }
      return null;
    case 'ASD':
      return `${attributeContent}.assistance`;
    case 'PFI':
      return `${attributeContent}.financial-loss-guarantee`;
    case 'PAK':
    case 'PACK':
      return `${attributeContent}.extension-pack-guarantee-maintenance`;
    case 'GARANTIE':
      return `${attributeContent}.extension-guarantee`;
    case 'ENTRETIEN':
      return `${attributeContent}.maintenance`;
    case 'EXG':
      return `${attributeContent}.extension-guarantee`;
    default:
      return null;
  }
};

export const mapProjectInformationCard = (
  pomoNumber: string,
  contract: ContractResponse,
  locale: string,
  translateService: TranslateService
): RecapCard => {
  return {
    title: `${cardTitle}.project-information`,
    iconName: 'target',
    holderAttributeList: [
      {
        title: `${attributeTitle}.name-pos-number`,
        content: translateService.instant(`${attributeContent}.category-${contract.vehicle.category}`)
      },
      {
        title: `${attributeTitle}.name-pos-number`,
        content: `${contract.pointOfSale.vendorName} ${contract.pointOfSale.vendorNumber}`
      },
      {
        title: `${attributeTitle}.name-surname`,
        content: `${contract.pointOfSale.dcz} ${contract.pointOfSale.bac}`
      },
      {
        title: `${attributeTitle}.pomo-number`,
        content: pomoNumber
      },
      {
        title: `${attributeTitle}.product-type`,
        content: contract.offer.product?.productReference.commercialProduct
      },
      {
        title: `${attributeTitle}.vehicle-type`,
        content: translateService.instant(`${attributeContent}.${contract.vehicle.used ? 'old' : 'new'}-vehicle`)
      },
      {
        title: `${attributeTitle}.natcode`,
        content: contract.vehicle.vehicleId
      },
      {
        title: `${attributeTitle}.model`,
        content: contract.vehicle.model
      },
      {
        title: `${attributeTitle}.post-code`,
        content: `${contract.holder.physicalPerson?.address.postCode} - ${contract.holder.physicalPerson?.address.city}`
      },
      {
        title: `${attributeTitle}.lease-eligibility`,
        content: translateService.instant(`${attributeContent}.${contract.vehicle.unbrokenVATChain ? 'yes' : 'no'}`)
      },
      {
        title: `${attributeTitle}.contract-annual-mileage`,
        content: formatValue(contract.vehicle.annualMileage || 0, RecapCardFormat.KM, locale)
      },
      {
        title: `${attributeTitle}.funding-duration`,
        content: translateService.instant(attributeContentMonthDuration, {
          months: contract.offer.duration
        })
      },
      {
        title: `${attributeTitle}.catalog-price`,
        content: formatValue(contract.vehicle.catalogPriceAmount, RecapCardFormat.EUROS, locale)
      },
      {
        title: `${attributeTitle}.billed-price`,
        content: formatValue(contract.vehicle.purchasePriceAmount, RecapCardFormat.EUROS, locale)
      },
      {
        title: `${attributeTitle}.funded-price`,
        content: formatValue(contract.offer.overdraftAmount, RecapCardFormat.EUROS, locale)
      },
      {
        title: `${attributeTitle}.request-responsible`,
        content: `${contract.pointOfSale.bac} ${contract.pointOfSale.dcz}`
      },
      {
        title: `${attributeTitle}.insurances`,
        content: contract.insurances?.map(insurance => insurance.label).join(' ')
      }
    ]
  };
};

export const formatPersonName = (person: PhysicalPersonResponse | NoValue): string => {
  if (!person?.firstName && !person?.lastName) {
    return '';
  }
  const last = person.lastName?.toUpperCase();
  const first = person.firstName
    ?.split(/([ -])/g)
    .map(subName => {
      return subName.length > 1 ? subName.charAt(0).toUpperCase() + subName.slice(1).toLowerCase() : subName;
    })
    .join('');

  return `${last?.trim()} ${first?.trim()}`;
};

enum ActivityProSiren {
  AGRICULTURAL_SECTOR = 'SAG',
  LIBERAL_PROFESSIONS = 'PLI'
}

enum ProfessionNoSiren {
  SALARIE_AGRICOLE = '02',
  VRP_NOFIX = '25'
}

const addressingDtoToString = (addressing: AddressingResponse): string => {
  switch (addressing.meshNumber) {
    case MeshType.MESH_1:
      return addressing.meshSiret ?? '';
    case MeshType.MESH_2:
      return `${addressing.meshSiret}-${addressing.meshSiret}`;
    case MeshType.MESH_3:
      return `${addressing.meshSiret}-${addressing.meshSiret}-${addressing.meshSiret}`;
    case MeshType.MESH_4:
      return `${addressing.meshSiret}-${addressing.meshSiret}`;
  }
};
